/**
 * Social: Instagram, Threads en X (24-10-2026, Jordy). Ontwerp: docs/ontwerp/social-fase2-3.md.
 * 30-10-2026: Instagram "Content kiezen" per account (map vrijgeven, inplannen, bijschriften; § 14).
 * 2-11-2026: kiezen per foto ("Use selected photos", max. 10) = vrijgave per bestand, alleen
 * foto's die aan de Meta-eisen voldoen ("Show all" toont de afgevallen met hun reden), map
 * automatisch als er precies één geschikte is, per gekozen foto een kaart met tekst en tags en
 * goedkeuren per post, knoppen die uit staan tonen hun reden, tab "Posted" met "Check on
 * Instagram" voor een onzeker resultaat (alleen lezen), en de status "onzeker".
 *
 * Per platform een tab. Accounts met noodstop, posts (foto, carrousel, reel, story, tekst)
 * met verplichte goedkeuring ("Approved by" met naam en tijd) vóór "Process now", reacties
 * op eigen posts en de goedkeuringsrij. Alles staat standaard in dry-run: er gaat niets
 * naar een platform. Tijden: invoer in Amsterdamse tijd, opslag in UTC (server),
 * weergave in Amsterdamse tijd met de zone erbij.
 */
import { useState } from 'react'
import type { ReactNode } from 'react'
import { ErrorNote, Loading, Panel, Seg, Table, Tag } from '../components/ui'
import { Modal, Field } from '../components/Modal'
import { useApi } from '../lib/useApi'
import type { Query } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { api, thumbUrl } from '../lib/api'

type Platform = 'instagram' | 'threads' | 'x'
interface Tijd { utc: string; amsterdam: string; zone: string }
interface Regels {
  platform: Platform; label: string; dry_run: boolean; soorten: string[]
  carrousel_min: number; carrousel_max: number; max_tekst: number; posts_per_24u: number
  antwoorden_per_24u: number | null; verwijderingen_per_24u: number | null; verbergen: boolean
  auto_antwoorden: boolean; dm_venster_uur: number | null; bronnen: string[]; opmerking: string
  kosten: string[]
}
interface Overzicht {
  platforms: Regels[]; uitgesloten: string[]; uitgesloten_reden: string
  ik: { beheer: boolean; goedkeuren: boolean }
}
export interface Account {
  id: number; platform: Platform; naam: string; gebruikersnaam: string; platform_user_id: string | null
  model_persona_id: number | null; status: 'new' | 'active' | 'paused' | 'error'; noodstop: boolean
  token_aanwezig: boolean | null; token_vervalt_op: Tijd | null; dagbudget: number; grens_24u: number
  verwerkt_24u: number; auto_reacties_aan: boolean; auto_max_per_uur: number; auto_max_per_dag: number
  /** 30-10: Instagram "Content kiezen": de aangewezen map en het tempo (max. 3 per dag). */
  content_folder_id?: number | null; posts_per_dag?: number; eigen_tagsets?: Record<string, string[]> | null
  /** 2-11: knop "Posted (n)": gepubliceerde en onzekere bestanden in de tab Posted (niet verwijderd). */
  geplaatst_aantal?: number; onzeker_aantal?: number
}
interface Post {
  id: number; account_id: number; soort: string; media: number[]; bijschrift: string
  gepland_op: Tijd | null; status: string; dry_run: boolean; resultaat_id: string | null; fout: string | null
  goedgekeurd_door: string | null; goedgekeurd_op: Tijd | null; verwerkt_op: Tijd | null
  vrijgave_id?: number | null; caption_tekst?: string | null; tagset?: string | null; tags?: string[]
  container_id?: string | null
}
interface Reactie {
  id: number; account_id: number; auteur: string; tekst: string; eigen: boolean; verborgen: boolean
  ouder_id: string | null; geplaatst_op: Tijd | null
}
interface Actie {
  id: number; account_id: number; comment_id: number; soort: string; tekst: string; bron: string
  status: string; goedgekeurd_door: string | null; goedgekeurd_op: Tijd | null; fout: string | null
}
interface Media { id: number; titel: string; type: string; map_id: number | null }
/** Een model van dit merk uit /api/admin/models/personas (eigen database per merk). */
interface Model { id: number; name: string; stage_name: string | null; slug: string;
  fancentro_model_id: string | null; f2f_model_id: string | null }

const modelLabel = (m: Model) =>
  `${m.stage_name || m.name}${m.fancentro_model_id ? ' (FC)' : m.f2f_model_id ? ' (F2F)' : ''}`

export const tijd = (t: Tijd | null) => (t ? `${t.amsterdam} ${t.zone}` : '—')

const KIND: Record<string, 'ok' | 'warn' | 'bad' | 'mut' | 'acc'> = {
  scheduled: 'acc', processing: 'warn', dry_run: 'mut', published: 'ok', failed: 'bad', cancelled: 'mut',
  onzeker: 'warn',
  new: 'mut', active: 'ok', paused: 'warn', error: 'bad',
  voorstel: 'acc', afgewezen: 'mut', uitgevoerd: 'ok',
}
const SOORT_LABEL: Record<string, string> = {
  foto: 'Photo', carrousel: 'Carousel', reel: 'Reel / video', story: 'Story', tekst: 'Text',
}
/** Statussen komen als sleutel van de server; op het scherm in het Engels. */
const STATUS_LABEL: Record<string, string> = {
  voorstel: 'proposal', afgewezen: 'rejected', uitgevoerd: 'done', dry_run: 'dry run',
  processing: 'processing', failed: 'failed',
}
/** 26-10 (Jordy): alleen "Published (live)" staat echt op het platform; dry run, mislukt en
 *  geannuleerd zeggen er "not posted" bij, zodat niemand denkt dat iets live staat. */
const POST_STATUS: Record<string, { kind: 'ok' | 'warn' | 'bad' | 'mut' | 'acc'; tekst: string; uitleg: string }> = {
  published: { kind: 'ok', tekst: 'Published (live)', uitleg: 'This post is on the platform.' },
  scheduled: { kind: 'acc', tekst: 'Scheduled', uitleg: 'Waiting. Needs Approve and Process now.' },
  processing: { kind: 'warn', tekst: 'Processing', uitleg: 'Being processed right now.' },
  dry_run: { kind: 'mut', tekst: 'Dry run – not posted', uitleg: 'Processed as a test run. Nothing was sent to the platform.' },
  failed: { kind: 'bad', tekst: 'Failed – not posted', uitleg: 'Not posted. See the error.' },
  cancelled: { kind: 'mut', tekst: 'Cancelled – not posted', uitleg: 'Cancelled before it was posted.' },
  // 2-11: media_publish gaf geen duidelijk antwoord; nooit opnieuw verwerkt, alleen nakijken.
  onzeker: { kind: 'warn', tekst: 'Uncertain – check on Instagram',
             uitleg: 'Instagram gave no clear answer. It is not retried. An owner or admin can press Check on Instagram (Posted tab).' },
}

function PostStatusTag({ status }: { status: string }) {
  const s = POST_STATUS[status] ?? { kind: 'mut' as const, tekst: status, uitleg: '' }
  return <Tag kind={s.kind} title={s.uitleg}>{s.tekst}</Tag>
}

const ACTIE_LABEL: Record<string, string> = {
  antwoord: 'Reply', verbergen: 'Hide', tonen: 'Unhide', verwijderen: 'Delete',
}

export default function Instagram() {
  const overzicht = useApi<Overzicht>('/api/admin/social/overview')
  const [platform, setPlatform] = useState<Platform>('instagram')
  const cfg = overzicht.data?.platforms.find((p) => p.platform === platform)
  const ik = overzicht.data?.ik ?? { beheer: false, goedkeuren: false }
  // 26-10 (Jordy): filter per model. Alleen modellen van accounts die deze gebruiker mag zien
  // (de server geeft alleen die accounts terug); de namen komen uit de modellenlijst van dit merk.
  const [modelId, setModelId] = useState(0)
  const alleAccounts = useApi<{ accounts: Account[] }>('/api/admin/social/accounts')
  const modellen = useApi<Model[]>('/api/admin/models/personas?split=true')
  const modelIds = Array.from(new Set((alleAccounts.data?.accounts ?? [])
    .map((a) => a.model_persona_id).filter((x): x is number => typeof x === 'number')))
  const naamModel = (id: number) => {
    const m = (modellen.data ?? []).find((x) => x.id === id)
    return m ? modelLabel(m) : `#${id}`
  }

  return (
    <div>
      <div className="row" style={{ marginBottom: 12 }}>
        <Seg value={platform} onChange={(v) => setPlatform(v as Platform)}
             options={[{ value: 'instagram', label: 'Instagram' }, { value: 'threads', label: 'Threads' },
                       { value: 'x', label: 'X' }]} />
        <select className="inp" style={{ width: 'auto', marginLeft: 8 }} value={modelId}
                onChange={(e) => setModelId(Number(e.target.value))} data-testid="social-modelfilter"
                aria-label="Model">
          <option value={0}>All models</option>
          {modelIds.map((id) => <option key={id} value={id}>{naamModel(id)}</option>)}
        </select>
      </div>
      {overzicht.error ? <ErrorNote error={overzicht.error} /> : null}
      {overzicht.loading && !overzicht.data ? <Loading /> : null}
      {cfg && overzicht.data ? (
        <>
          <Uitleg cfg={cfg} o={overzicht.data} />
          <PlatformTab key={`${platform}-${modelId}`} cfg={cfg} ik={ik} modelId={modelId} />
        </>
      ) : null}
    </div>
  )
}

function Uitleg({ cfg, o }: { cfg: Regels; o: Overzicht }) {
  return (
    <div className="note" style={{ marginBottom: 12 }}>
      {cfg.dry_run
        ? <><b>Dry run.</b> Nothing is sent to {cfg.label}. </>
        : <span style={{ color: 'var(--bad)' }}><b>{cfg.label} is configured as live.</b> </span>}
      Every post needs <b>Approve</b> (someone has looked at the media) and then <b>Process now</b>.
      {' '}Instagram and Threads do not allow nudity or sexual solicitation: check every image and caption yourself.
      {' '}Limit on {cfg.label}: {cfg.posts_per_24u} posts per 24 h
      {cfg.antwoorden_per_24u ? `, ${cfg.antwoorden_per_24u} replies per 24 h` : ''}
      {cfg.verwijderingen_per_24u ? `, ${cfg.verwijderingen_per_24u} deletions per 24 h` : ''}.
      {cfg.opmerking ? <> {cfg.opmerking}</> : null}
      {' '}Direct messages: {cfg.dm_venster_uur
        ? `design and dry run only; only replies to people who messaged first, within ${cfg.dm_venster_uur} h of their last message.`
        : 'not part of this design.'}
      {cfg.kosten.length ? (
        <details style={{ marginTop: 6 }} open>
          <summary>Costs of the {cfg.label} API (not purchased; needs the owner's approval)</summary>
          <ul style={{ margin: '6px 0 0 18px' }}>{cfg.kosten.map((k) => <li key={k}>{k}</li>)}</ul>
        </details>
      ) : null}
      <details style={{ marginTop: 6 }}>
        <summary>What this page will never do</summary>
        <ul style={{ margin: '6px 0 0 18px' }}>
          {o.uitgesloten.map((u) => <li key={u}>{u}</li>)}
        </ul>
        <div className="mut2">{o.uitgesloten_reden}</div>
        <div className="mut2" style={{ marginTop: 4 }}>
          Sources: {cfg.bronnen.map((b, i) => (
            <span key={b}>{i ? ', ' : ''}<a href={b} target="_blank" rel="noreferrer">{i + 1}</a></span>
          ))}
        </div>
      </details>
    </div>
  )
}

function PlatformTab({ cfg, ik, modelId }: { cfg: Regels; ik: Overzicht['ik']; modelId: number }) {
  const p = cfg.platform
  const accounts = useApi<{ accounts: Account[] }>(`/api/admin/social/accounts?platform=${p}`, [p])
  const modelQs = modelId ? `&model_persona_id=${modelId}` : ''
  const posts = useApi<{ posts: Post[] }>(`/api/admin/social/posts?platform=${p}${modelQs}`, [p, modelId])
  const lijst = (accounts.data?.accounts ?? []).filter((a) => !modelId || a.model_persona_id === modelId)
  const gefilterd: Query<{ accounts: Account[] }> = { ...accounts, data: accounts.data ? { accounts: lijst } : null }
  const [gekozen, setGekozen] = useState<Account | null>(null)
  const [kiezen, setKiezen] = useState<{ account: Account; tab: KiezenTab } | null>(null)
  const herlaad = () => { accounts.refetch(); posts.refetch() }
  return (
    <>
      <Accounts cfg={cfg} ik={ik} q={gefilterd} onKies={setGekozen}
                onContent={p === 'instagram' ? (a) => setKiezen({ account: a, tab: 'kiezen' }) : undefined}
                onPosted={p === 'instagram' ? (a) => setKiezen({ account: a, tab: 'geplaatst' }) : undefined} />
      {kiezen ? <ContentKiezen key={`k-${kiezen.account.id}-${kiezen.tab}`} account={kiezen.account} start={kiezen.tab}
                               beheer={ik.beheer} onSluit={() => setKiezen(null)} herlaadAlles={herlaad} /> : null}
      {gekozen ? <AccountWeergave key={gekozen.id} cfg={cfg} ik={ik} account={gekozen}
                                  onSluit={() => setGekozen(null)} herlaadAlles={herlaad} /> : null}
      <Wachtrij cfg={cfg} ik={ik} accounts={lijst} q={posts} herlaad={herlaad} />
      <Reacties cfg={cfg} accounts={lijst} />
      <Goedkeuringsrij cfg={cfg} ik={ik} accounts={lijst} />
      <AutoRegels cfg={cfg} ik={ik} accounts={lijst} />
      <Statistieken cfg={cfg} accounts={lijst} />
    </>
  )
}

// ── accounts ────────────────────────────────────────────────────────────────

/** 2-11 (Jordy): "Posted (3)" of "Posted (3 · 1 uncertain)"; onzeker alleen als het er is. */
function postedLabel(a: Account): string {
  const n = a.geplaatst_aantal ?? 0
  const onzeker = a.onzeker_aantal ?? 0
  return onzeker > 0 ? `Posted (${n} · ${onzeker} uncertain)` : `Posted (${n})`
}

function Accounts({ cfg, ik, q, onKies, onContent, onPosted }: {
  cfg: Regels; ik: Overzicht['ik']; q: Query<{ accounts: Account[] }>; onKies?: (a: Account) => void
  onContent?: (a: Account) => void; onPosted?: (a: Account) => void
}) {
  const { run, busy } = useActions()
  const [nieuw, setNieuw] = useState(false)
  const [bewerk, setBewerk] = useState<Account | null>(null)
  return (
    <Panel title="Accounts" desc={`${cfg.label} accounts per model. Tokens are never shown; they live only on the server.`}
           right={ik.beheer ? <button className="btn sm" type="button" onClick={() => setNieuw(true)}>+ Account</button> : null}>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      <Table cols={[
        { key: 'naam', label: 'Account' }, { key: 'status', label: 'Status' },
        { key: 'stop', label: 'Emergency stop' }, { key: 'token', label: 'Token' },
        { key: 'budget', label: 'Last 24 h / limit', num: true }, { key: 'act', label: '' },
      ]} emptyText={`No ${cfg.label} accounts yet.`}>
        {(q.data?.accounts ?? []).map((a) => (
          <tr key={a.id}>
            <td><button className="btn ghost sm" type="button" title="Open the posts of this account"
                        data-testid={`account-open-${a.id}`} onClick={() => onKies?.(a)}>{a.naam} ›</button>
              <div className="mut2">@{a.gebruikersnaam}</div></td>
            <td><Tag kind={KIND[a.status] ?? 'mut'}>{a.status}</Tag></td>
            <td>{a.noodstop ? <Tag kind="bad">on</Tag> : <Tag kind="ok">off</Tag>}</td>
            <td title="The web service never reads tokens; a separate worker will (not built yet).">kept on the server, not checked here</td>
            <td className="num">{a.verwerkt_24u} / {a.grens_24u}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {onContent ? <><button className="btn sm" type="button" data-testid={`content-kiezen-${a.id}`}
                                     title="Choose photos from the Instagram folder of this model and plan them"
                                     onClick={() => onContent(a)}>Choose content</button>{' '}</> : null}
              {onPosted ? <><button className="btn sm" type="button" data-testid={`posted-${a.id}`}
                                    title="Everything the bot posted on this account, and posts with an uncertain result"
                                    onClick={() => onPosted(a)}>{postedLabel(a)}</button>{' '}</> : null}
              {!a.noodstop ? (
                <button className="btn sm" type="button" disabled={!!busy}
                        onClick={() => run({ label: 'Emergency stop', confirm: `Stop everything for ${a.naam}?`,
                                             request: mut.post(`/api/admin/social/accounts/${a.id}/emergency-stop`),
                                             onDone: q.refetch, successText: 'Stopped' })}>Emergency stop</button>
              ) : null}
              {ik.beheer ? <> <button className="btn ghost sm" type="button" onClick={() => setBewerk(a)}>Edit</button></> : null}
            </td>
          </tr>
        ))}
      </Table>
      {nieuw ? <AccountDialoog cfg={cfg} onClose={() => setNieuw(false)} onDone={() => { setNieuw(false); q.refetch() }} /> : null}
      {bewerk ? <AccountDialoog cfg={cfg} account={bewerk} onClose={() => setBewerk(null)}
                                onDone={() => { setBewerk(null); q.refetch() }} /> : null}
    </Panel>
  )
}

function AccountDialoog({ cfg, account, onClose, onDone }: {
  cfg: Regels; account?: Account; onClose: () => void; onDone: () => void
}) {
  const { run, busy } = useActions()
  const [naam, setNaam] = useState(account?.naam ?? '')
  const [gebruiker, setGebruiker] = useState(account?.gebruikersnaam ?? '')
  const [userId, setUserId] = useState(account?.platform_user_id ?? '')
  const [model, setModel] = useState(account?.model_persona_id ? String(account.model_persona_id) : '')
  // 26-10 (Jordy): kies een model op naam in plaats van een id in te typen.
  const modellen = useApi<Model[]>('/api/admin/models/personas?split=true&active_only=true&linked_only=true')
  const [budget, setBudget] = useState(account?.dagbudget ?? 10)
  const [noodstop, setNoodstop] = useState(account?.noodstop ?? true)
  const [auto, setAuto] = useState(account?.auto_reacties_aan ?? false)
  const [perUur, setPerUur] = useState(account?.auto_max_per_uur ?? 5)
  const [perDag, setPerDag] = useState(account?.auto_max_per_dag ?? 20)
  const opslaan = () => {
    const basis = { platform_user_id: userId.trim(), model_persona_id: model ? Number(model) : null, dagbudget: budget }
    run(account ? {
      label: `Save ${account.naam}`,
      confirm: account.noodstop && !noodstop ? 'Turn the emergency stop OFF for this account?' : undefined,
      request: mut.patch(`/api/admin/social/accounts/${account.id}`, {
        ...basis, gebruikersnaam: gebruiker.trim(), auto_reacties_aan: auto,
        auto_max_per_uur: perUur, auto_max_per_dag: perDag,
        // 2-11 (Jordy): only send the emergency stop when it was changed in this form,
        // so saving Edit never flips it silently.
        ...(noodstop !== account.noodstop ? { noodstop } : {}),
      }),
      onDone, successText: 'Saved',
    } : {
      label: 'New account',
      request: mut.post('/api/admin/social/accounts', { ...basis, platform: cfg.platform, naam: naam.trim(), gebruikersnaam: gebruiker.trim() }),
      onDone, successText: 'Account added (emergency stop on)',
    })
  }
  return (
    <Modal title={account ? `Edit ${account.naam}` : `New ${cfg.label} account`} width={520} onClose={onClose}
           footer={<button className="btn primary sm" type="button" disabled={!!busy || (!account && (!naam.trim() || !gebruiker.trim()))}
                           onClick={opslaan}>Save</button>}>
      {!account ? <Field label="Name"><input className="inp" value={naam} onChange={(e) => setNaam(e.target.value)} /></Field> : null}
      <Field label="Username"><input className="inp" value={gebruiker} onChange={(e) => setGebruiker(e.target.value)} /></Field>
      <Field label="Platform user id" hint="The numeric id, not the username. The token on the server is stored under this id.">
        <input className="inp" value={userId} inputMode="numeric" onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))} />
      </Field>
      <Field label="Model" hint="Models of this brand. Media for posts come from this model's content.">
        {modellen.error ? <ErrorNote error={modellen.error} /> : null}
        <select className="inp" value={model} onChange={(e) => setModel(e.target.value)} data-testid="social-model">
          <option value="">Choose a model…</option>
          {(modellen.data ?? []).map((m) => <option key={m.id} value={String(m.id)}>{modelLabel(m)}</option>)}
          {model && !(modellen.data ?? []).some((m) => String(m.id) === model)
            ? <option value={model}>#{model} (not in the list)</option> : null}
        </select>
      </Field>
      <Field label="Daily post budget" hint={`Platform maximum: ${cfg.posts_per_24u} per 24 h; the lower one applies.`}>
        <input className="inp" type="number" min={0} max={100} value={budget} onChange={(e) => setBudget(Number(e.target.value))} />
      </Field>
      {account ? (
        <>
          <Field label="Emergency stop" hint="While on, nothing is processed for this account.">
            <label><input type="checkbox" checked={noodstop} onChange={(e) => setNoodstop(e.target.checked)} /> On</label>
          </Field>
          <Field label="Auto replies (proposals)" hint={cfg.auto_antwoorden
            ? 'Keyword templates create proposals; nothing is sent without approval.'
            : `Not allowed on ${cfg.label}.`}>
            <label><input type="checkbox" checked={auto} disabled={!cfg.auto_antwoorden}
                          onChange={(e) => setAuto(e.target.checked)} /> On</label>
            <div className="row" style={{ marginTop: 6 }}>
              <label>Max per hour <input className="inp" type="number" min={0} max={60} value={perUur} style={{ width: 70 }}
                                         onChange={(e) => setPerUur(Number(e.target.value))} /></label>
              <label>Max per day <input className="inp" type="number" min={0} max={500} value={perDag} style={{ width: 70 }}
                                        onChange={(e) => setPerDag(Number(e.target.value))} /></label>
            </div>
          </Field>
        </>
      ) : <div className="mut2">A new account starts with the emergency stop on and auto replies off.</div>}
    </Modal>
  )
}

// ── posts ───────────────────────────────────────────────────────────────────

function Wachtrij({ cfg, ik, accounts, q, herlaad }: {
  cfg: Regels; ik: Overzicht['ik']; accounts: Account[]
  q: Query<{ posts: Post[] }>; herlaad: () => void
}) {
  const { run, busy } = useActions()
  const [nieuw, setNieuw] = useState(false)
  const naamVan = (id: number) => accounts.find((a) => a.id === id)?.naam ?? `#${id}`
  return (
    <Panel title="Queue" desc="Every post needs Approve and then Process now. Times are Amsterdam time."
           right={<button className="btn primary sm" type="button" onClick={() => setNieuw(true)}
                          disabled={!accounts.length}>+ New post</button>}>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      <Table cols={[
        { key: 'acc', label: 'Account' }, { key: 'soort', label: 'Type' }, { key: 'when', label: 'Planned' },
        { key: 'cap', label: 'Text' }, { key: 'ok', label: 'Approved by' }, { key: 'status', label: 'Status' },
        { key: 'act', label: '' },
      ]} emptyText="Nothing in the queue.">
        {(q.data?.posts ?? []).map((p) => (
          <tr key={p.id}>
            <td>{naamVan(p.account_id)}</td>
            <td>{SOORT_LABEL[p.soort] ?? p.soort}{p.media.length > 1 ? ` (${p.media.length})` : ''}</td>
            <td>{tijd(p.gepland_op)}</td>
            <td title={p.bijschrift} style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.bijschrift || '—'}
            </td>
            <td>{p.goedgekeurd_door ? <>{p.goedgekeurd_door}<div className="mut2">{tijd(p.goedgekeurd_op)}</div></> : '—'}</td>
            <td>
              <PostStatusTag status={p.status} />
              {p.fout ? <div className="mut2" style={{ fontSize: 11 }}>{p.fout}</div> : null}
            </td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {p.status === 'scheduled' ? (
                <>
                  {!p.goedgekeurd_op && ik.goedkeuren ? (
                    <button className="btn sm" type="button" disabled={!!busy}
                            onClick={() => run({ label: `Approve post ${p.id}`,
                                                 confirm: 'I have looked at the media and the text myself. Approve?',
                                                 request: mut.post(`/api/admin/social/posts/${p.id}/approve`),
                                                 onDone: herlaad, successText: 'Approved' })}>Approve</button>
                  ) : null}
                  {p.goedgekeurd_op ? (
                    <button className="btn sm" type="button" disabled={!!busy}
                            onClick={() => run({ label: `Process post ${p.id}`,
                                                 confirm: cfg.dry_run ? 'Process now? This is a dry run: nothing is sent.'
                                                   : `Process now? This publishes to ${cfg.label}.`,
                                                 request: mut.post(`/api/admin/social/posts/${p.id}/process`),
                                                 onDone: herlaad, successText: 'Processed' })}>Process now</button>
                  ) : null}{' '}
                  <button className="btn ghost sm" type="button" disabled={!!busy}
                          onClick={() => run({ label: `Cancel post ${p.id}`, confirm: 'Cancel this post?',
                                               request: mut.post(`/api/admin/social/posts/${p.id}/cancel`),
                                               onDone: herlaad, successText: 'Cancelled' })}>Cancel</button>
                </>
              ) : null}
              {p.status === 'onzeker' && ik.beheer ? (
                <button className="btn sm" type="button" disabled={!!busy}
                        title="Only reads the status on Instagram; never posts again."
                        onClick={() => run({ label: `Check post ${p.id} on Instagram`,
                                             request: mut.post(`/api/admin/social/posts/${p.id}/check`),
                                             onDone: herlaad, successText: 'Checked' })}>Check on Instagram</button>
              ) : null}
            </td>
          </tr>
        ))}
      </Table>
      {nieuw ? <NieuwePost cfg={cfg} accounts={accounts} onClose={() => setNieuw(false)}
                           onDone={() => { setNieuw(false); herlaad() }} /> : null}
    </Panel>
  )
}

function NieuwePost({ cfg, accounts, onClose, onDone }: {
  cfg: Regels; accounts: Account[]; onClose: () => void; onDone: () => void
}) {
  const { run, busy } = useActions()
  const [accountId, setAccountId] = useState<number>(accounts[0]?.id ?? 0)
  const [soort, setSoort] = useState(cfg.soorten[0])
  const [gekozen, setGekozen] = useState<number[]>([])
  const [bijschrift, setBijschrift] = useState('')
  const [wanneer, setWanneer] = useState<'nu' | 'gepland'>('gepland')
  const [lokaal, setLokaal] = useState('')
  const media = useApi<{ items: Media[]; social_mappen?: number }>(accountId ? `/api/admin/social/accounts/${accountId}/media` : null, [accountId])
  const meer = soort === 'carrousel'
  const nodig = soort === 'tekst' ? 0 : 1
  const mediaOk = meer ? gekozen.length >= cfg.carrousel_min && gekozen.length <= cfg.carrousel_max
    : gekozen.length === nodig
  const klaar = accountId && mediaOk && (wanneer === 'nu' || lokaal) && (soort !== 'tekst' || bijschrift.trim())
  const kies = (id: number) => setGekozen((g) => meer ? (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]) : [id])
  const opslaan = () => run({
    label: 'New post',
    request: mut.post('/api/admin/social/posts', {
      account_id: accountId, soort, content_item_ids: gekozen, bijschrift,
      nu: wanneer === 'nu', gepland_lokaal: wanneer === 'gepland' ? lokaal : null,
    }),
    onDone, successText: 'Added to the queue (still needs Approve)',
  })
  return (
    <Modal title={`New ${cfg.label} post`} width={600} onClose={onClose}
           footer={<button className="btn primary sm" type="button" disabled={!klaar || !!busy} onClick={opslaan}>Add to queue</button>}>
      <Field label="Account">
        <select className="inp" value={accountId} onChange={(e) => { setAccountId(Number(e.target.value)); setGekozen([]) }}>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.naam} (@{a.gebruikersnaam})</option>)}
        </select>
      </Field>
      <Field label="Type">
        <Seg value={soort} onChange={(v) => { setSoort(v); setGekozen([]) }}
             options={cfg.soorten.map((s) => ({ value: s, label: SOORT_LABEL[s] ?? s }))} />
      </Field>
      {soort !== 'tekst' ? (
        <Field label={meer ? `Media (${cfg.carrousel_min}–${cfg.carrousel_max}, in this order)` : 'Media'}
               hint="Only this model's content from folders marked Social media, never explicit. Check it yourself.">
          {media.loading && !media.data ? <Loading /> : null}
          {media.data && !media.data.social_mappen
            ? <div className="mut2">No folder is marked for social media yet, so no media can be chosen.</div>
            : null}
          <div style={{ maxHeight: 220, overflow: 'auto' }}>
            {(media.data?.items ?? []).map((m) => (
              <label key={m.id} style={{ display: 'block' }}>
                <input type={meer ? 'checkbox' : 'radio'} checked={gekozen.includes(m.id)} onChange={() => kies(m.id)} />
                {' '}#{m.id} {m.titel} ({m.type}){meer && gekozen.includes(m.id) ? ` — ${gekozen.indexOf(m.id) + 1}` : ''}
              </label>
            ))}
          </div>
        </Field>
      ) : null}
      <Field label={soort === 'tekst' ? 'Text' : 'Caption'} hint={`${bijschrift.length} / ${cfg.max_tekst}`}>
        <textarea className="inp" rows={4} maxLength={cfg.max_tekst} value={bijschrift} onChange={(e) => setBijschrift(e.target.value)} />
      </Field>
      <Field label="When" hint="Amsterdam time. Stored in UTC on the server. Nothing goes out on its own: Approve and Process now are still needed.">
        <div className="row">
          <label><input type="radio" checked={wanneer === 'nu'} onChange={() => setWanneer('nu')} /> Now</label>
          <label><input type="radio" checked={wanneer === 'gepland'} onChange={() => setWanneer('gepland')} /> Scheduled</label>
          {wanneer === 'gepland' ? <input className="inp" type="datetime-local" value={lokaal} onChange={(e) => setLokaal(e.target.value)} /> : null}
        </div>
      </Field>
    </Modal>
  )
}

// ── reacties (fase A) ───────────────────────────────────────────────────────

function Reacties({ cfg, accounts }: { cfg: Regels; accounts: Account[] }) {
  const p = cfg.platform
  const q = useApi<{ comments: Reactie[] }>(`/api/admin/social/comments?platform=${p}`, [p])
  const { run, busy } = useActions()
  const [antwoord, setAntwoord] = useState<Reactie | null>(null)
  const naamVan = (id: number) => accounts.find((a) => a.id === id)?.naam ?? `#${id}`
  return (
    <Panel title="Comments" desc="Comments on our own posts. Fetching is a button, not a live call on every page view."
           inklap="social-reacties"
           right={<div className="row">{accounts.map((a) => (
             <button key={a.id} className="btn sm" type="button" disabled={!!busy || a.noodstop}
                     title={a.noodstop ? 'Emergency stop is on' : undefined}
                     onClick={() => run({ label: `Fetch comments ${a.naam}`,
                                          request: mut.post(`/api/admin/social/accounts/${a.id}/comments/fetch`),
                                          onDone: q.refetch, successText: 'Fetched' })}>Fetch {a.naam}</button>
           ))}</div>}>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      <Table cols={[
        { key: 'acc', label: 'Account' }, { key: 'from', label: 'From' }, { key: 'tekst', label: 'Comment' },
        { key: 'wanneer', label: 'Posted' }, { key: 'act', label: '' },
      ]} emptyText={cfg.dry_run ? 'No comments. In dry run nothing is fetched.' : 'No comments yet.'}>
        {(q.data?.comments ?? []).map((c) => (
          <tr key={c.id}>
            <td>{naamVan(c.account_id)}</td>
            <td>{c.eigen ? <Tag kind="acc">us</Tag> : `@${c.auteur}`}{c.ouder_id ? <div className="mut2">reply</div> : null}</td>
            <td style={{ maxWidth: 360 }}>{c.tekst}{c.verborgen ? <> <Tag kind="warn">hidden</Tag></> : null}</td>
            <td>{tijd(c.geplaatst_op)}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {!c.eigen ? <button className="btn sm" type="button" disabled={!!busy} onClick={() => setAntwoord(c)}>Reply</button> : null}{' '}
              {cfg.verbergen ? (
                <button className="btn ghost sm" type="button" disabled={!!busy}
                        onClick={() => run({ label: c.verborgen ? 'Unhide' : 'Hide',
                                             request: mut.post(`/api/admin/social/comments/${c.id}/hide`, { verbergen: !c.verborgen }),
                                             onDone: q.refetch })}>{c.verborgen ? 'Unhide' : 'Hide'}</button>
              ) : null}{' '}
              <button className="btn ghost sm" type="button" disabled={!!busy}
                      onClick={() => run({ label: 'Delete comment', confirm: `Delete this comment on ${cfg.label}? This cannot be undone.`,
                                           request: mut.post(`/api/admin/social/comments/${c.id}/delete`), onDone: q.refetch })}>Delete</button>
            </td>
          </tr>
        ))}
      </Table>
      {antwoord ? <AntwoordDialoog cfg={cfg} reactie={antwoord} onClose={() => setAntwoord(null)}
                                   onDone={() => { setAntwoord(null); q.refetch() }} /> : null}
    </Panel>
  )
}

function AntwoordDialoog({ cfg, reactie, onClose, onDone }: {
  cfg: Regels; reactie: Reactie; onClose: () => void; onDone: () => void
}) {
  const { run, busy } = useActions()
  const [tekst, setTekst] = useState('')
  return (
    <Modal title={`Reply to @${reactie.auteur}`} width={520} onClose={onClose}
           footer={<button className="btn primary sm" type="button" disabled={!tekst.trim() || !!busy}
                           onClick={() => run({ label: 'Reply', request: mut.post(`/api/admin/social/comments/${reactie.id}/reply`, { tekst }),
                                                onDone, successText: cfg.dry_run ? 'Reply recorded (dry run)' : 'Reply sent' })}>Send reply</button>}>
      <div className="note" style={{ marginBottom: 8 }}>{reactie.tekst}</div>
      <Field label="Your reply" hint={`${tekst.length} / ${cfg.max_tekst}. Written by you; no AI.`}>
        <textarea className="inp" rows={4} maxLength={cfg.max_tekst} value={tekst} onChange={(e) => setTekst(e.target.value)} />
      </Field>
    </Modal>
  )
}

// ── goedkeuringsrij (fase A) ────────────────────────────────────────────────

function Goedkeuringsrij({ cfg, ik, accounts }: { cfg: Regels; ik: Overzicht['ik']; accounts: Account[] }) {
  const p = cfg.platform
  const q = useApi<{ actions: Actie[] }>(`/api/admin/social/actions?platform=${p}`, [p])
  const { run, busy } = useActions()
  const naamVan = (id: number) => accounts.find((a) => a.id === id)?.naam ?? `#${id}`
  const voorstellen = (q.data?.actions ?? []).filter((a) => a.status === 'voorstel')
  const gedaan = (q.data?.actions ?? []).filter((a) => a.status !== 'voorstel').slice(0, 50)
  return (
    <Panel title="Approval queue" inklap="social-goedkeuring"
           desc="Proposals wait here until someone with approval rights presses Approve. Below: the last actions.">
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      <Table cols={[
        { key: 'acc', label: 'Account' }, { key: 'soort', label: 'Action' }, { key: 'tekst', label: 'Text' },
        { key: 'bron', label: 'Source' }, { key: 'act', label: '' },
      ]} emptyText="No proposals waiting.">
        {voorstellen.map((a) => (
          <tr key={a.id}>
            <td>{naamVan(a.account_id)}</td>
            <td>{ACTIE_LABEL[a.soort] ?? a.soort}</td>
            <td style={{ maxWidth: 360 }}>{a.tekst || '—'}</td>
            <td>{a.bron === 'auto' ? 'template' : 'manual'}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {ik.goedkeuren ? (
                <>
                  <button className="btn sm" type="button" disabled={!!busy}
                          onClick={() => run({ label: `Approve ${a.id}`, confirm: 'Approve and send this reply?',
                                               request: mut.post(`/api/admin/social/actions/${a.id}/approve`), onDone: q.refetch })}>Approve</button>{' '}
                  <button className="btn ghost sm" type="button" disabled={!!busy}
                          onClick={() => run({ label: `Reject ${a.id}`, request: mut.post(`/api/admin/social/actions/${a.id}/reject`),
                                               onDone: q.refetch })}>Reject</button>
                </>
              ) : <span className="mut2">needs approval</span>}
            </td>
          </tr>
        ))}
      </Table>
      {gedaan.length ? (
        <Table cols={[
          { key: 'acc', label: 'Account' }, { key: 'soort', label: 'Action' }, { key: 'status', label: 'Status' },
          { key: 'door', label: 'By' }, { key: 'wanneer', label: 'When' },
        ]}>
          {gedaan.map((a) => (
            <tr key={a.id}>
              <td>{naamVan(a.account_id)}</td>
              <td>{ACTIE_LABEL[a.soort] ?? a.soort}{a.bron === 'auto' ? ' (template)' : ''}</td>
              <td><Tag kind={KIND[a.status] ?? 'mut'} title={a.fout ?? undefined}>{STATUS_LABEL[a.status] ?? a.status}</Tag></td>
              <td>{a.goedgekeurd_door ?? '—'}</td>
              <td>{tijd(a.goedgekeurd_op)}</td>
            </tr>
          ))}
        </Table>
      ) : null}
    </Panel>
  )
}

// ── automatische reacties (fase B) ──────────────────────────────────────────

interface Regel { id: number; account_id: number; trefwoord: string; sjabloon: string; aan: boolean }

function AutoRegels({ cfg, ik, accounts }: { cfg: Regels; ik: Overzicht['ik']; accounts: Account[] }) {
  const p = cfg.platform
  const q = useApi<{ rules: Regel[] }>(`/api/admin/social/rules?platform=${p}`, [p])
  const { run, busy } = useActions()
  const [gekozenId, setAccountId] = useState<number>(0)
  // De accounts komen pas na het laden binnen: zonder keuze geldt het eerste account.
  const accountId = gekozenId || accounts[0]?.id || 0
  const [trefwoord, setTrefwoord] = useState('')
  const [sjabloon, setSjabloon] = useState('')
  const naamVan = (id: number) => accounts.find((a) => a.id === id)?.naam ?? `#${id}`
  if (!cfg.auto_antwoorden) {
    return (
      <Panel title="Auto replies" inklap="social-auto">
        <div className="mut2">Not available on {cfg.label}: its automation rules do not allow keyword-based automatic replies.</div>
      </Panel>
    )
  }
  return (
    <Panel title="Auto replies" inklap="social-auto"
           desc="Keyword → fixed template → a proposal in the approval queue. Nothing is sent without Approve. No AI. Off by default, per account and per rule.">
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      <Table cols={[
        { key: 'acc', label: 'Account' }, { key: 'kw', label: 'Keyword' }, { key: 'tpl', label: 'Template' },
        { key: 'aan', label: 'On' }, { key: 'act', label: '' },
      ]} emptyText="No rules.">
        {(q.data?.rules ?? []).map((r) => (
          <tr key={r.id}>
            <td>{naamVan(r.account_id)}{accounts.find((a) => a.id === r.account_id)?.auto_reacties_aan ? null
              : <div className="mut2">auto replies off for this account</div>}</td>
            <td>{r.trefwoord}</td>
            <td style={{ maxWidth: 360 }}>{r.sjabloon}</td>
            <td>{r.aan ? <Tag kind="ok">on</Tag> : <Tag kind="mut">off</Tag>}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {ik.goedkeuren ? (
                <>
                  <button className="btn ghost sm" type="button" disabled={!!busy}
                          onClick={() => run({ label: r.aan ? 'Rule off' : 'Rule on',
                                               request: mut.patch(`/api/admin/social/rules/${r.id}`, { aan: !r.aan }), onDone: q.refetch })}>
                    {r.aan ? 'Turn off' : 'Turn on'}</button>{' '}
                  <button className="btn ghost sm" type="button" disabled={!!busy}
                          onClick={() => run({ label: 'Delete rule', confirm: 'Delete this rule?',
                                               request: mut.del(`/api/admin/social/rules/${r.id}`), onDone: q.refetch })}>Delete</button>
                </>
              ) : null}
            </td>
          </tr>
        ))}
      </Table>
      {ik.goedkeuren && accounts.length ? (
        <div className="row" style={{ marginTop: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <select className="inp" value={accountId} onChange={(e) => setAccountId(Number(e.target.value))}>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
          </select>
          <input className="inp" placeholder="Keyword" value={trefwoord} maxLength={100} onChange={(e) => setTrefwoord(e.target.value)} />
          <input className="inp" placeholder="Template text" value={sjabloon} maxLength={cfg.max_tekst} style={{ minWidth: 280 }}
                 onChange={(e) => setSjabloon(e.target.value)} />
          <button className="btn sm" type="button" disabled={!!busy || !trefwoord.trim() || !sjabloon.trim()}
                  onClick={() => run({ label: 'New rule', request: mut.post('/api/admin/social/rules', {
                    account_id: accountId, trefwoord, sjabloon, aan: false }),
                    onDone: () => { setTrefwoord(''); setSjabloon(''); q.refetch() }, successText: 'Rule added (off)' })}>Add rule (off)</button>
        </div>
      ) : null}
    </Panel>
  )
}

// ── statistieken (fase C) ───────────────────────────────────────────────────

interface Stat { account_id: number; post_id: number | null; media_id: string | null; metriek: string; waarde: number; gemeten_op: Tijd | null }

const METRIEK_LABEL: Record<string, string> = {
  followers_count: 'Followers', media_count: 'Posts', reach: 'Reach', likes: 'Likes', comments: 'Comments',
  saved: 'Saved', shares: 'Shares', views: 'Views', replies: 'Replies', reposts: 'Reposts', quotes: 'Quotes',
}

function Statistieken({ cfg, accounts }: { cfg: Regels; accounts: Account[] }) {
  const p = cfg.platform
  const q = useApi<{ stats: Stat[] }>(`/api/admin/social/stats?platform=${p}`, [p])
  const { run, busy } = useActions()
  const naamVan = (id: number) => accounts.find((a) => a.id === id)?.naam ?? `#${id}`
  const rijen = q.data?.stats ?? []
  const perAccount = rijen.filter((r) => !r.media_id)
  const perPost = new Map<string, Stat[]>()
  for (const r of rijen.filter((x) => x.media_id)) {
    const k = `${r.account_id}:${r.media_id}`
    perPost.set(k, [...(perPost.get(k) ?? []), r])
  }
  const kolommen = Array.from(new Set(rijen.filter((r) => r.media_id).map((r) => r.metriek)))
  return (
    <Panel title="Statistics" inklap="social-stats"
           desc="Stored measurements with their time. This page never calls the platform; press Measure to fetch new numbers. Meta data can be up to 48 h behind."
           right={<div className="row">{accounts.map((a) => (
             <button key={a.id} className="btn sm" type="button" disabled={!!busy || a.noodstop}
                     title={a.noodstop ? 'Emergency stop is on' : undefined}
                     onClick={() => run({ label: `Measure ${a.naam}`, request: mut.post(`/api/admin/social/accounts/${a.id}/stats/fetch`),
                                          onDone: q.refetch, successText: 'Measured' })}>Measure {a.naam}</button>
           ))}</div>}>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      <Table cols={[{ key: 'acc', label: 'Account' }, { key: 'm', label: 'Metric' },
                    { key: 'v', label: 'Value', num: true }, { key: 't', label: 'Measured' }]}
             emptyText={cfg.dry_run ? 'No measurements. In dry run nothing is fetched.' : 'No measurements yet.'}>
        {perAccount.map((r) => (
          <tr key={`${r.account_id}-${r.metriek}`}>
            <td>{naamVan(r.account_id)}</td><td>{METRIEK_LABEL[r.metriek] ?? r.metriek}</td>
            <td className="num">{r.waarde.toLocaleString('en-GB')}</td><td>{tijd(r.gemeten_op)}</td>
          </tr>
        ))}
      </Table>
      {perPost.size ? (
        <Table cols={[{ key: 'acc', label: 'Account' }, { key: 'post', label: 'Post' },
                      ...kolommen.map((k) => ({ key: k, label: METRIEK_LABEL[k] ?? k, num: true })),
                      { key: 't', label: 'Measured' }]}>
          {Array.from(perPost.entries()).map(([k, rs]) => (
            <tr key={k}>
              <td>{naamVan(rs[0].account_id)}</td>
              <td>{rs[0].post_id ? `#${rs[0].post_id}` : rs[0].media_id}</td>
              {kolommen.map((kol) => <td key={kol} className="num">{rs.find((r) => r.metriek === kol)?.waarde.toLocaleString('en-GB') ?? '—'}</td>)}
              <td>{tijd(rs[0].gemeten_op)}</td>
            </tr>
          ))}
        </Table>
      ) : null}
    </Panel>
  )
}

// ── weergave per account (ontwerp goedgekeurd door Jordy 26-10) ─────────────

/** De Amsterdamse kalenderdag ('YYYY-MM-DD') van een moment. */
const amsDag = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' })

/** De zeven dagen (ma t/m zo) van de week `offset` weken vanaf deze week, Amsterdamse tijd. */
function weekVan(offset: number): string[] {
  const vandaag = new Date(`${amsDag(new Date())}T12:00:00Z`)
  const maandag = vandaag.getTime() - ((vandaag.getUTCDay() + 6) % 7) * 86400000 + offset * 7 * 86400000
  return Array.from({ length: 7 }, (_, i) => new Date(maandag + i * 86400000).toISOString().slice(0, 10))
}

const weekdag = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', timeZone: 'UTC' })
const STATUS_FILTERS = ['scheduled', 'processing', 'dry_run', 'published', 'failed', 'cancelled']

function AccountWeergave({ cfg, ik, account, onSluit, herlaadAlles }: {
  cfg: Regels; ik: Overzicht['ik']; account: Account; onSluit: () => void; herlaadAlles: () => void
}) {
  const { run, busy } = useActions()
  const [week, setWeek] = useState(0)
  const [statussen, setStatussen] = useState<string[]>([])
  const [soorten, setSoorten] = useState<string[]>([])
  const [bewerk, setBewerk] = useState<Post | null>(null)
  const [nieuw, setNieuw] = useState(false)
  const dagen = weekVan(week)
  const qs = new URLSearchParams({ platform: cfg.platform, account_id: String(account.id), van: dagen[0], tot: dagen[6] })
  if (statussen.length) qs.set('status', statussen.join(','))
  if (soorten.length) qs.set('soort', soorten.join(','))
  const q = useApi<{ posts: Post[] }>(`/api/admin/social/posts?${qs.toString()}`, [qs.toString()])
  const posts = (q.data?.posts ?? []).slice()
    .sort((a, b) => (a.gepland_op?.utc ?? '').localeCompare(b.gepland_op?.utc ?? ''))
  const herlaad = () => { q.refetch(); herlaadAlles() }
  const wissel = (lijst: string[], zet: (l: string[]) => void, w: string) =>
    zet(lijst.includes(w) ? lijst.filter((x) => x !== w) : [...lijst, w])
  return (
    <Panel title={`Account ${account.naam}`}
           desc={<>@{account.gebruikersnaam} · {cfg.label} · week of {dagen[0]} (Amsterdam time).{' '}
             <b>Only "Published (live)" is on {cfg.label}.</b> Dry run, failed and cancelled posts were not posted.</>}
           right={<div className="row">
             <button className="btn ghost sm" type="button" onClick={() => setWeek(week - 1)}>‹ Previous week</button>
             <button className="btn ghost sm" type="button" onClick={() => setWeek(0)} disabled={week === 0}>This week</button>
             <button className="btn ghost sm" type="button" onClick={() => setWeek(week + 1)}>Next week ›</button>
             <button className="btn primary sm" type="button" onClick={() => setNieuw(true)}>+ New post</button>
             <button className="btn ghost sm" type="button" onClick={onSluit}>Close</button>
           </div>}>
      <div className="row" style={{ flexWrap: 'wrap', gap: 10, marginBottom: 8 }} data-testid="weergave-filters">
        <span className="mut2">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <label key={s}><input type="checkbox" checked={statussen.includes(s)}
                                onChange={() => wissel(statussen, setStatussen, s)} /> {POST_STATUS[s].tekst}</label>
        ))}
        <span className="mut2" style={{ marginLeft: 8 }}>Type:</span>
        {cfg.soorten.map((s) => (
          <label key={s}><input type="checkbox" checked={soorten.includes(s)}
                                onChange={() => wissel(soorten, setSoorten, s)} /> {SOORT_LABEL[s] ?? s}</label>
        ))}
      </div>
      {q.loading && !q.data ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      <div data-testid="weergave-kalender"
           style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 6, marginBottom: 10 }}>
        {dagen.map((d) => (
          <div key={d} className="card" style={{ padding: 6, minHeight: 70 }}>
            <div className="mut2" style={{ fontSize: 11 }}>{weekdag(d)} {d.slice(8, 10)}-{d.slice(5, 7)}</div>
            {posts.filter((p) => p.gepland_op?.amsterdam.slice(0, 10) === d).map((p) => (
              <div key={p.id} style={{ fontSize: 12, marginTop: 4 }} title={p.bijschrift}>
                <b>{p.gepland_op?.amsterdam.slice(11)}</b> {SOORT_LABEL[p.soort] ?? p.soort}
                <div><PostStatusTag status={p.status} /></div>
              </div>
            ))}
          </div>
        ))}
      </div>
      <Table cols={[
        { key: 'm', label: '' }, { key: 'when', label: 'Planned' }, { key: 'soort', label: 'Type' },
        { key: 'cap', label: 'Text' }, { key: 'status', label: 'Status' }, { key: 'ok', label: 'Approved by' },
        { key: 'act', label: '' },
      ]} emptyText="No posts in this week.">
        {posts.map((p) => (
          <tr key={p.id}>
            <td>{p.media[0]
              ? <img src={thumbUrl(p.media[0])} alt="" width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} />
              : <span className="mut2">—</span>}</td>
            <td>{tijd(p.gepland_op)}</td>
            <td>{SOORT_LABEL[p.soort] ?? p.soort}{p.media.length > 1 ? ` (${p.media.length})` : ''}</td>
            <td style={{ maxWidth: 300 }}>{p.bijschrift || '—'}</td>
            <td><PostStatusTag status={p.status} />{p.fout ? <div className="mut2" style={{ fontSize: 11 }}>{p.fout}</div> : null}</td>
            <td>{p.goedgekeurd_door ? <>{p.goedgekeurd_door}<div className="mut2">{tijd(p.goedgekeurd_op)}</div></>
              : p.status === 'scheduled' ? <span className="mut2">not approved yet</span> : '—'}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              {p.status === 'scheduled' ? (
                <>
                  <button className="btn ghost sm" type="button" disabled={!!busy} onClick={() => setBewerk(p)}>Edit</button>{' '}
                  {!p.goedgekeurd_op && ik.goedkeuren ? (
                    <button className="btn sm" type="button" disabled={!!busy}
                            onClick={() => run({ label: `Approve post ${p.id}`,
                                                 confirm: 'I have looked at the media and the text myself. Approve?',
                                                 request: mut.post(`/api/admin/social/posts/${p.id}/approve`),
                                                 onDone: herlaad, successText: 'Approved' })}>Approve</button>
                  ) : null}
                  {p.goedgekeurd_op ? (
                    <button className="btn sm" type="button" disabled={!!busy}
                            onClick={() => run({ label: `Process post ${p.id}`,
                                                 confirm: cfg.dry_run ? 'Process now? This is a dry run: nothing is sent.'
                                                   : `Process now? This publishes to ${cfg.label}.`,
                                                 request: mut.post(`/api/admin/social/posts/${p.id}/process`),
                                                 onDone: herlaad, successText: 'Processed' })}>Process now</button>
                  ) : null}{' '}
                  <button className="btn ghost sm" type="button" disabled={!!busy}
                          onClick={() => run({ label: `Cancel post ${p.id}`, confirm: 'Cancel this post?',
                                               request: mut.post(`/api/admin/social/posts/${p.id}/cancel`),
                                               onDone: herlaad, successText: 'Cancelled' })}>Cancel</button>
                </>
              ) : null}
            </td>
          </tr>
        ))}
      </Table>
      {bewerk ? <BewerkPost cfg={cfg} post={bewerk} account={account} onClose={() => setBewerk(null)}
                            onDone={() => { setBewerk(null); herlaad() }} /> : null}
      {nieuw ? <NieuwePost cfg={cfg} accounts={[account]} onClose={() => setNieuw(false)}
                           onDone={() => { setNieuw(false); herlaad() }} /> : null}
    </Panel>
  )
}

function BewerkPost({ cfg, post, account, onClose, onDone }: {
  cfg: Regels; post: Post; account: Account; onClose: () => void; onDone: () => void
}) {
  const { run, busy } = useActions()
  const [bijschrift, setBijschrift] = useState(post.bijschrift)
  const [wanneer, setWanneer] = useState<'houd' | 'nu' | 'gepland'>('houd')
  const [lokaal, setLokaal] = useState(post.gepland_op ? post.gepland_op.amsterdam.replace(' ', 'T') : '')
  const [gekozen, setGekozen] = useState<number[]>(post.media)
  const tekst = post.soort === 'tekst'
  const media = useApi<{ items: Media[] }>(tekst ? null : `/api/admin/social/accounts/${account.id}/media`, [account.id])
  const meer = post.soort === 'carrousel'
  const kies = (id: number) => setGekozen((g) => meer ? (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]) : [id])
  const opslaan = () => run({
    label: `Save post ${post.id}`,
    request: mut.patch(`/api/admin/social/posts/${post.id}`, {
      bijschrift, content_item_ids: tekst ? null : gekozen,
      nu: wanneer === 'nu', gepland_lokaal: wanneer === 'gepland' ? lokaal : null,
    }),
    onDone, successText: 'Saved. The post must be approved again.',
  })
  return (
    <Modal title={`Edit ${(SOORT_LABEL[post.soort] ?? post.soort).toLowerCase()} post`} width={600} onClose={onClose}
           footer={<button className="btn primary sm" type="button" disabled={!!busy || (wanneer === 'gepland' && !lokaal)}
                           onClick={opslaan}>Save (needs approval again)</button>}>
      <div className="note" data-testid="opnieuw-goedkeuren" style={{ marginBottom: 10, borderColor: 'var(--warn)' }}>
        <b>Saving a change removes the approval.</b>{' '}
        {post.goedgekeurd_door ? `It is now approved by ${post.goedgekeurd_door}. ` : ''}
        Someone must look at the post again and press Approve before it can be processed.
      </div>
      {!tekst ? (
        <Field label={meer ? `Media (${cfg.carrousel_min}–${cfg.carrousel_max}, in this order)` : 'Media'}
               hint="Only this model's content from folders marked Social media.">
          {media.loading && !media.data ? <Loading /> : null}
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            {(media.data?.items ?? []).map((m) => (
              <label key={m.id} style={{ display: 'block' }}>
                <input type={meer ? 'checkbox' : 'radio'} checked={gekozen.includes(m.id)} onChange={() => kies(m.id)} />
                {' '}#{m.id} {m.titel} ({m.type})
              </label>
            ))}
          </div>
        </Field>
      ) : null}
      <Field label={tekst ? 'Text' : 'Caption'} hint={`${bijschrift.length} / ${cfg.max_tekst}`}>
        <textarea className="inp" rows={4} maxLength={cfg.max_tekst} value={bijschrift} onChange={(e) => setBijschrift(e.target.value)} />
      </Field>
      <Field label="When" hint={`Now planned: ${tijd(post.gepland_op)}. Amsterdam time.`}>
        <div className="row">
          <label><input type="radio" checked={wanneer === 'houd'} onChange={() => setWanneer('houd')} /> Keep</label>
          <label><input type="radio" checked={wanneer === 'nu'} onChange={() => setWanneer('nu')} /> Now</label>
          <label><input type="radio" checked={wanneer === 'gepland'} onChange={() => setWanneer('gepland')} /> New time</label>
          {wanneer === 'gepland' ? <input className="inp" type="datetime-local" value={lokaal} onChange={(e) => setLokaal(e.target.value)} /> : null}
        </div>
      </Field>
    </Modal>
  )
}

// ── Instagram: Content kiezen (30-10-2026; tweede ronde 2-11-2026, Jordy) ─────
//
// Map automatisch als er precies één geschikte is (JPEG's direct in de map). Foto's aanklikken
// (max. 10) en "Use selected photos": die keuze is de vrijgave per bestand. Daarna per gekozen
// foto een kaart met tekst en tags, opslaan als concept en goedkeuren per post. Elke knop die
// niet kan, staat uit met de reden ernaast (de reden komt van de server).

interface KiesItem {
  id: number; titel: string; nieuw: boolean; gepland: boolean
  /** Gekozen (vrijgegeven per bestand, ongewijzigd), of na het kiezen gewijzigd. */
  vrijgegeven: boolean; gewijzigd: boolean
  /** Meta-eisen per bestand; reden is een korte Engelse code ("3:4", ">8 MB", "other ratio 0.70"). */
  voldoet: boolean; reden: string | null; reden_groep: string | null
}
interface Vrijgave { id: number; door: string; op: Tijd | null; aantal: number; map_id: number | null }
interface Redenen { kiezen: string | null; goedkeuren: string | null; voorbeeld: string | null }
interface KiesData {
  account_id: number; posts_per_dag: number; max_posts_per_dag: number; max_keuze: number; max_vrijgave: number
  voldoet: number; afgewezen: Record<string, number>; gekozen: number; redenen: Redenen
  map: { id: number; naam: string; automatisch: boolean } | null; reden: string | null
  kandidaat_mappen: { id: number; naam: string; jpeg: number }[]; vrijgave: Vrijgave | null; items: KiesItem[]
  uitgesloten: { niet_jpeg: number; ontbreekt: number; label: number; verborgen: number }
  ik: { vrijgeven: boolean; inplannen: boolean }
}
interface Einde { soort: 'link' | 'more' | null; zin: string }
interface Kaart {
  content_item_id: number; titel: string; caption: string; tags: string[]; trefwoorden: string[]
  opgeslagen: boolean; gekozen_door: string | null
}
interface KaartData {
  kaarten: Kaart[]; bron: string; volgende_einde: Einde; thema_tags: string[]; min_tags: number; max_tags: number
  redenen: Redenen
}
interface BijschriftCfg {
  link_zinnen: string[]; meer_zinnen: string[]; link_aandeel: number; meer_aandeel: number; verboden: string[]
  bron: Record<string, string>; min_tags: number; max_tags: number; thema_tags: string[]
}

const vandaagAms = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' })
/** 2-11 (Jordy): de dag na `dag` (YYYY-MM-DD), als kalenderdag (geen tijdzone-rekenwerk). */
const dagNa = (dag: string) => {
  const [j, m, d] = dag.split('-').map(Number)
  return new Date(Date.UTC(j, m - 1, d + 1)).toISOString().slice(0, 10)
}
/** 2-11 (Jordy): waarom de eerste dag niet bruikbaar is; null = goed. Een leeg of ongeldig veld
 *  wordt nooit stil "morgen": de server weigert het ook. */
const eersteDagReden = (start: string, vandaag: string): string | null =>
  !start ? 'Choose the first day'
    : !/^\d{4}-\d{2}-\d{2}$/.test(start) || Number.isNaN(Date.parse(start)) ? 'Invalid first day (use YYYY-MM-DD)'
    : start < vandaag ? 'The first day is in the past' : null

/** 2-11 (Jordy): zoveel foto's per ronde kiezen (de server staat er ook max. 10 toe). */
const MAX_KEUZE_PER_RONDE = 10

/** Uitleg bij de redencodes van de server (meta_eisen.py). */
const REDEN_UITLEG: Record<string, string> = {
  '3:4': 'Portrait 3:4 is taller than Instagram allows (4:5 at most)',
  '9:16': 'Portrait 9:16 (story size) is too tall for a feed photo',
  '2:3': 'Portrait 2:3 is taller than Instagram allows (4:5 at most)',
  'other ratio': 'Aspect ratio outside 4:5 to 1.91:1',
  '>8 MB': 'Larger than 8 MB',
  'not JPEG': 'Not a real JPEG file',
  MPO: 'MPO (multi-picture) file',
  '<320 wide': 'Narrower than 320 pixels',
}

const EINDE_LABEL = (e: Einde) => e.soort === 'link' ? `link in bio: "${e.zin}"`
  : e.soort === 'more' ? `"${e.zin}" (no link)` : 'nothing (no link)'

/** Een knop die uit staat als er een reden is, met die reden zichtbaar ernaast. */
function KnopMetReden({ reden, onClick, children, primair, testid }: {
  reden: string | null; onClick: () => void; children: ReactNode; primair?: boolean; testid?: string
}) {
  const { busy } = useActions()
  return (
    <span className="row" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <button className={primair ? 'btn primary sm' : 'btn sm'} type="button" disabled={!!reden || !!busy}
              onClick={onClick} data-testid={testid} title={reden ?? undefined}>{children}</button>
      {reden ? <span className="mut2" style={{ fontSize: 12 }} data-testid={testid ? `${testid}-reden` : undefined}>
        {reden}</span> : null}
    </span>
  )
}

type KiezenTab = 'kiezen' | 'geplaatst'

function ContentKiezen({ account, start = 'kiezen', beheer, onSluit, herlaadAlles }: {
  account: Account; start?: KiezenTab; beheer: boolean; onSluit: () => void; herlaadAlles: () => void
}) {
  const [tab, setTab] = useState<KiezenTab>(start)
  return (
    <Panel title={`Instagram — ${account.naam}`}
           desc={<>Only JPEG photos from the Instagram folder of this model that meet the Instagram rules. An owner
             clicks the photos for this round (at most {MAX_KEUZE_PER_RONDE}) and presses <b>Use selected photos</b>:
             that choice is the approval of those files. Then every chosen photo gets its own text and tags, and each
             post is approved on its own. Every photo is posted at most once per account (also a copy or a renamed
             file). Scheduling never posts by itself: every post still needs <b>Process now</b>, and the emergency
             stop, dry run and the live list per account still apply.</>}
           right={<div className="row">
             <Seg value={tab} onChange={(v) => setTab(v as KiezenTab)}
                  options={[{ value: 'kiezen', label: 'Choose content' }, { value: 'geplaatst', label: 'Posted' }]} />
             <button className="btn ghost sm" type="button" onClick={onSluit}>Close</button>
           </div>}>
      {tab === 'kiezen'
        ? <KiesTab account={account} beheer={beheer} herlaadAlles={herlaadAlles} />
        : <Geplaatst account={account} onGewijzigd={herlaadAlles} />}
    </Panel>
  )
}

function KiesTab({ account, beheer, herlaadAlles }: {
  account: Account; beheer: boolean; herlaadAlles: () => void
}) {
  const { run, busy } = useActions()
  const q = useApi<KiesData>(`/api/admin/social/accounts/${account.id}/content`, [account.id])
  const kaarten = useApi<KaartData>(`/api/admin/social/accounts/${account.id}/chosen`, [account.id])
  const vandaag = vandaagAms()
  const komend = useApi<{ posts: Post[] }>(
    `/api/admin/social/posts?platform=instagram&account_id=${account.id}&van=${vandaag}`, [account.id])
  const [keuze, setKeuze] = useState<number[]>([])
  const [toonAlles, setToonAlles] = useState(false)
  const [start, setStart] = useState(() => dagNa(vandaagAms()))
  const [melding, setMelding] = useState<string | null>(null)
  const [voorbeeld, setVoorbeeld] = useState<{ eindes: { nr: number; soort: Einde['soort']; zin: string }[] } | null>(null)
  const d = q.data
  const maxKeuze = Math.min(MAX_KEUZE_PER_RONDE, d?.max_vrijgave ?? MAX_KEUZE_PER_RONDE)
  const herlaad = () => { q.refetch(); kaarten.refetch(); komend.refetch(); herlaadAlles() }
  const kies = (id: number) => setKeuze((g) => g.includes(id) ? g.filter((x) => x !== id)
    : g.length >= maxKeuze ? g : [...g, id])
  const afgewezen = (d?.items ?? []).filter((it) => !it.voldoet)
  const zichtbaar = (d?.items ?? []).filter((it) => toonAlles || it.voldoet)
  const gebruik = () => run({
    label: 'Use selected photos',
    confirm: `I have checked these ${keuze.length} photos myself. Use them for Instagram?`,
    request: mut.post(`/api/admin/social/accounts/${account.id}/folder-release`, { content_item_ids: keuze }),
    onDone: () => { setKeuze([]); herlaad() }, successText: 'Photos chosen',
  })
  const toonVoorbeeld = () => run({
    label: 'Caption endings preview',
    request: async () => {
      setVoorbeeld(await api.get(`/api/admin/social/accounts/${account.id}/captions/preview?aantal=10`))
    },
    successText: 'Preview ready',
  })
  const keuzeReden = d ? (d.redenen.kiezen ?? (keuze.length ? null : 'Select one or more photos first')) : 'Loading'
  const geplandePosts = (komend.data?.posts ?? []).slice()
    .sort((a, b) => (a.gepland_op?.utc ?? '').localeCompare(b.gepland_op?.utc ?? ''))
  const startReden = eersteDagReden(start, vandaag)
  const toonMapKeuze = !!d && beheer && d.kandidaat_mappen.length > 0 && (!d.map || d.kandidaat_mappen.length > 1)
  return (
    <>
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: 6 }}>
        <KnopMetReden reden={d ? d.redenen.voorbeeld : 'Loading'} onClick={toonVoorbeeld} testid="caption-voorbeeld">
          Preview next 10 caption endings</KnopMetReden>
      </div>
      {q.loading && !d ? <Loading /> : null}
      {q.error ? <ErrorNote error={q.error} /> : null}
      {d ? (
        <>
          <div className="row" style={{ flexWrap: 'wrap', gap: 10, marginBottom: 8 }}>
            <span>Instagram folder: <b>{d.map ? d.map.naam : 'none'}</b>
              {d.map?.automatisch ? <> <Tag kind="mut" title="The only folder of this model with JPEG photos marked Social media">
                chosen automatically</Tag></> : null}</span>
            {toonMapKeuze ? (
              <select className="inp" style={{ width: 'auto' }} value={d.map?.id ?? ''} data-testid="instagram-map"
                      aria-label="Instagram folder"
                      onChange={(e) => run({
                        label: 'Instagram folder',
                        request: mut.post(`/api/admin/social/accounts/${account.id}/instagram-folder`,
                          { content_folder_id: e.target.value ? Number(e.target.value) : null }),
                        onDone: () => { setKeuze([]); herlaad() }, successText: 'Folder saved',
                      })}>
                <option value="">Choose a folder…</option>
                {d.kandidaat_mappen.map((m) => <option key={m.id} value={m.id}>{m.naam} ({m.jpeg} JPEG)</option>)}
              </select>
            ) : null}
            <span className="mut2">
              {d.vrijgave ? <>Last chosen by {d.vrijgave.door} on {tijd(d.vrijgave.op)} ({d.vrijgave.aantal} photos)</>
                : 'No photos chosen yet'}
            </span>
            <label className="mut2">Posts per day{' '}
              <select className="inp" style={{ width: 'auto' }} value={d.posts_per_dag} disabled={!beheer || !!busy}
                      aria-label="Posts per day" data-testid="posts-per-dag"
                      onChange={(e) => run({
                        label: 'Posts per day',
                        request: mut.patch(`/api/admin/social/accounts/${account.id}`, { posts_per_dag: Number(e.target.value) }),
                        onDone: herlaad, successText: 'Saved',
                      })}>
                {Array.from({ length: d.max_posts_per_dag }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          {d.reden ? <div className="note" style={{ marginBottom: 8 }}>{d.reden}.
            {d.kandidaat_mappen.length ? '' : ' Mark the folder with the photos Social media on the Content page.'}</div> : null}
          {d.uitgesloten.niet_jpeg || d.uitgesloten.ontbreekt || d.uitgesloten.label ? (
            <div className="mut2" style={{ marginBottom: 6 }}>
              Not shown: {d.uitgesloten.niet_jpeg} not JPEG, {d.uitgesloten.ontbreekt} missing on the NAS,
              {' '}{d.uitgesloten.label} labelled more explicit than soft.
            </div>
          ) : null}
          {d.map && !d.items.length && !d.reden ? <div className="mut2">No JPEG photos in this folder.</div> : null}
          {d.items.length ? (
            <div className="row" style={{ flexWrap: 'wrap', gap: 10, marginBottom: 6 }}>
              <span className="mut2"><b>{d.voldoet}</b> meet the Instagram rules · {afgewezen.length} rejected</span>
              <label className="mut2"><input type="checkbox" checked={toonAlles} data-testid="toon-alles"
                                             onChange={(e) => setToonAlles(e.target.checked)} /> Show all</label>
              {toonAlles && afgewezen.length ? (
                <span className="mut2" data-testid="afgewezen-redenen">
                  {Object.entries(d.afgewezen).sort((a, b) => b[1] - a[1]).map(([r, n]) => (
                    <span key={r} title={REDEN_UITLEG[r] ?? r} style={{ marginRight: 8 }}>{r}: <b>{n}</b></span>
                  ))}
                  <span>Not cropped automatically: crop it yourself and put the new file in the folder.</span>
                </span>
              ) : null}
            </div>
          ) : null}
          <div data-testid="kies-raster"
               style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 6, maxHeight: 360, overflow: 'auto' }}>
            {zichtbaar.map((it) => {
              const kiesbaar = it.voldoet && !it.vrijgegeven && !it.gepland && d.ik.vrijgeven
              const aan = keuze.includes(it.id)
              const titel = !it.voldoet ? `Rejected: ${REDEN_UITLEG[it.reden_groep ?? ''] ?? it.reden ?? 'does not meet the rules'}`
                : it.gepland ? 'Already planned or posted (also as a copy)'
                : it.vrijgegeven ? 'Chosen: write its text below'
                : it.gewijzigd ? 'Changed since it was chosen: choose it again'
                : it.titel
              return (
                <label key={it.id} title={titel}
                       style={{ position: 'relative', opacity: kiesbaar || aan ? 1 : 0.55,
                                cursor: kiesbaar ? 'pointer' : 'not-allowed',
                                outline: aan ? '2px solid var(--acc)' : 'none', borderRadius: 4 }}>
                  <img src={thumbUrl(it.id)} alt="" loading="lazy"
                       style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, display: 'block' }} />
                  <input type="checkbox" checked={aan} disabled={!kiesbaar} onChange={() => kies(it.id)}
                         style={{ position: 'absolute', top: 4, left: 4 }} aria-label={`Choose ${it.titel}`} />
                  <span style={{ position: 'absolute', bottom: 4, left: 4 }}>
                    {!it.voldoet ? <Tag kind="bad">{it.reden}</Tag> : null}
                    {it.voldoet && it.gepland ? <Tag kind="mut">planned</Tag> : null}
                    {it.voldoet && !it.gepland && it.vrijgegeven ? <Tag kind="acc">chosen</Tag> : null}
                    {it.voldoet && !it.gepland && it.gewijzigd ? <Tag kind="warn">changed</Tag> : null}
                  </span>
                </label>
              )
            })}
          </div>
          <div className="row" style={{ marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
            <span className="mut2">{keuze.length} / {maxKeuze} selected</span>
            <KnopMetReden reden={keuzeReden} onClick={gebruik} primair testid="gebruik-selectie">
              Use selected photos ({keuze.length})</KnopMetReden>
          </div>
        </>
      ) : null}
      {voorbeeld ? (
        <div className="note" style={{ marginTop: 12 }} data-testid="voorbeeld-lijst">
          <b>How the next 10 posts end</b>
          <span className="mut2"> (only 1 in 4 gets the link in bio; the rest never points to the bio)</span>
          <ol style={{ margin: '6px 0 0 18px' }}>
            {voorbeeld.eindes.map((e) => <li key={e.nr}>{EINDE_LABEL(e)}</li>)}
          </ol>
          <button className="btn ghost sm" type="button" onClick={() => setVoorbeeld(null)}>Hide</button>
        </div>
      ) : null}
      <h4 style={{ margin: '14px 0 6px' }}>Chosen photos: text and tags per post</h4>
      {kaarten.error ? <ErrorNote error={kaarten.error} /> : null}
      {kaarten.data ? (
        <>
          <div className="mut2" style={{ marginBottom: 6 }}>
            Write the text yourself (or fill it from keywords) and pick tags that fit this photo
            ({kaarten.data.min_tags}–{kaarten.data.max_tags}). Do not add a link in bio yourself: the next approved post
            ends with {EINDE_LABEL(kaarten.data.volgende_einde)}. Saving and approving are checked against the banned list.
          </div>
          <label className="mut2">First day{' '}
            <input className="inp" type="date" min={vandaag} value={start} style={{ width: 'auto' }}
                   aria-label="First day" data-testid="eerste-dag"
                   onChange={(e) => { setStart(e.target.value); setMelding(null) }} /></label>
          <span className="mut2"> ({d?.posts_per_dag ?? 1} per day, at the next free time; today is allowed:
            then the first free time at least 30 minutes from now, Europe/Amsterdam)</span>
          {startReden ? <div className="note" style={{ marginTop: 6 }} data-testid="eerste-dag-reden">{startReden}</div> : null}
          {melding ? <div className="note" style={{ marginTop: 6 }} data-testid="doorgeschoven">{melding}</div> : null}
          {!kaarten.data.kaarten.length ? (
            <div className="mut2" style={{ marginTop: 6 }} data-testid="geen-kaarten">
              {kaarten.data.redenen.goedkeuren ?? 'No chosen photos waiting.'}</div>
          ) : null}
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {kaarten.data.kaarten.map((k) => (
              <FotoKaart key={k.content_item_id} account={account} kaart={k} data={kaarten.data as KaartData}
                         start={start} startReden={startReden} onMelding={setMelding} onDone={herlaad} />
            ))}
          </div>
        </>
      ) : null}
      <h4 style={{ margin: '14px 0 6px' }}>Planned posts from today</h4>
      {komend.error ? <ErrorNote error={komend.error} /> : null}
      <Table cols={[{ key: 'f', label: '' }, { key: 't', label: 'Planned' }, { key: 'c', label: 'Caption' },
                    { key: 's', label: 'Status' }, { key: 'a', label: '' }]} emptyText="Nothing planned.">
        {geplandePosts.map((p) => (
          <tr key={p.id}>
            <td>{p.media[0] ? <img src={thumbUrl(p.media[0])} alt="" width={40} height={40}
                                   style={{ objectFit: 'cover', borderRadius: 4 }} /> : null}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{p.status === 'scheduled' && d?.ik.inplannen
              ? <TijdVerzet post={p} onDone={herlaad} /> : tijd(p.gepland_op)}</td>
            <td style={{ maxWidth: 360 }} title={p.bijschrift}>{p.caption_tekst ?? p.bijschrift}
              {p.tags?.length ? <div className="mut2" style={{ fontSize: 11 }}>{p.tags.join(' ')}</div> : null}</td>
            <td><PostStatusTag status={p.status} /></td>
            <td>{p.status === 'scheduled' ? (
              <button className="btn ghost sm" type="button" disabled={!!busy}
                      onClick={() => run({ label: `Cancel post ${p.id}`, confirm: 'Cancel this post? The photo can be chosen again.',
                                           request: mut.post(`/api/admin/social/posts/${p.id}/cancel`),
                                           onDone: herlaad, successText: 'Cancelled' })}>Cancel</button>
            ) : null}</td>
          </tr>
        ))}
      </Table>
      <BijschriftInstellingen beheer={beheer} />
    </>
  )
}

const splitsTags = (t: string) => t.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean)
  .map((x) => (x.startsWith('#') ? x : `#${x}`))

/** 2-11 (Jordy): één geplande post los verzetten (datum + tijd, Europe/Amsterdam). De server
 *  controleert de regels en noemt elke overtreden regel; die staan dan hier onder het veld. */
function TijdVerzet({ post, onDone }: { post: Post; onDone: () => void }) {
  const { run, busy } = useActions()
  const [datum0, tijd0] = (post.gepland_op?.amsterdam ?? ' ').split(' ')
  const [datum, setDatum] = useState(datum0)
  const [klok, setKlok] = useState(tijd0)
  const [fouten, setFouten] = useState<string[]>([])
  const gewijzigd = datum !== datum0 || klok !== tijd0
  const reden = !/^\d{4}-\d{2}-\d{2}$/.test(datum) ? 'Choose a date'
    : !/^\d{2}:\d{2}$/.test(klok) ? 'Choose a time' : null
  const bewaar = () => run({
    label: `Move post ${post.id}`,
    request: async () => {
      try {
        await api.patch(`/api/admin/social/posts/${post.id}/time`, { datum, tijd: klok })
        setFouten([])
      } catch (e) {
        setFouten((e instanceof Error ? e.message : String(e)).split('; '))
        throw e
      }
    },
    onDone, successText: 'Time changed',
  })
  return (
    <div data-testid={`verzet-${post.id}`}>
      <div className="row" style={{ gap: 4, alignItems: 'center', flexWrap: 'nowrap' }}>
        <input className="inp" type="date" value={datum} style={{ width: 'auto' }} aria-label="Date"
               onChange={(e) => setDatum(e.target.value)} />
        <input className="inp" type="time" value={klok} style={{ width: 'auto' }} aria-label="Time"
               onChange={(e) => setKlok(e.target.value)} />
        <span className="mut2" style={{ fontSize: 11 }} title="Europe/Amsterdam">{post.gepland_op?.zone ?? 'CET/CEST'}</span>
        {gewijzigd ? <button className="btn sm" type="button" disabled={!!busy || !!reden} title={reden ?? undefined}
                             onClick={bewaar}>Save</button> : null}
      </div>
      {fouten.length ? (
        <ul className="mut2" style={{ margin: '4px 0 0 16px', fontSize: 12, color: '#fca5a5', whiteSpace: 'normal' }}
            data-testid={`move-errors-${post.id}`}>
          {fouten.map((f) => <li key={f}>{f}</li>)}
        </ul>
      ) : null}
    </div>
  )
}

function FotoKaart({ account, kaart, data, start, startReden, onMelding, onDone }: {
  account: Account; kaart: Kaart; data: KaartData; start: string; startReden: string | null
  onMelding: (m: string | null) => void; onDone: () => void
}) {
  const { run, notify } = useActions()
  const [caption, setCaption] = useState(kaart.caption)
  const [tags, setTags] = useState<string[]>(kaart.tags)
  const [woorden, setWoorden] = useState(kaart.trefwoorden.join(' '))
  const pad = `/api/admin/social/accounts/${account.id}/chosen/${kaart.content_item_id}`
  const wissel = (t: string) => setTags((l) => (l.some((x) => x.toLowerCase() === t.toLowerCase())
    ? l.filter((x) => x.toLowerCase() !== t.toLowerCase()) : [...l, t]))
  const tagReden = tags.length < data.min_tags || tags.length > data.max_tags
    ? `Use ${data.min_tags}–${data.max_tags} tags (now ${tags.length})` : null
  const tekstReden = !caption.trim() && !data.volgende_einde.soort ? 'Write a text first' : null
  const goedkeurReden = data.redenen.goedkeuren ?? startReden ?? tekstReden ?? tagReden
  const vul = () => run({
    label: 'Fill from keywords',
    request: async () => {
      const v = await api.post<{ caption: string; tags: string[] }>(`${pad}/suggest`, { trefwoorden: splitsTags(woorden).map((x) => x.slice(1)) })
      if (!caption.trim()) setCaption(v.caption)
      setTags((l) => [...l, ...v.tags.filter((t) => !l.some((x) => x.toLowerCase() === t.toLowerCase()))].slice(0, data.max_tags))
    },
    successText: 'Filled',
  })
  const bewaar = () => run({
    label: 'Save text',
    request: mut.post(`${pad}/text`, { caption, tags, trefwoorden: splitsTags(woorden).map((x) => x.slice(1)) }),
    successText: 'Saved',
  })
  const keurGoed = () => run({
    label: 'Approve post',
    confirm: 'I have looked at this photo, its text and its tags myself. Approve and schedule this post? '
      + 'Nothing is posted by this: it still needs Process now.',
    request: async () => {
      const uit = await api.post<{ melding?: string | null }>(`${pad}/approve`, { caption, tags, start_datum: start })
      onMelding(uit.melding ?? null)
      if (uit.melding) notify(uit.melding, true, { waarschuwing: true, duur: 10000 })
    },
    onDone, successText: 'Approved and scheduled',
  })
  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-start', border: '1px solid var(--line, #ddd)', borderRadius: 6, padding: 8 }}
         data-testid={`kaart-${kaart.content_item_id}`}>
      <img src={thumbUrl(kaart.content_item_id)} alt="" width={96} height={120}
           style={{ objectFit: 'cover', borderRadius: 4, flex: '0 0 auto' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="mut2" style={{ fontSize: 12 }}>{kaart.titel}{kaart.opgeslagen ? ' · draft saved' : ''}
          {kaart.gekozen_door ? ` · chosen by ${kaart.gekozen_door}` : ''}</div>
        <textarea className="inp" rows={2} value={caption} maxLength={2000} placeholder="Your text for this photo"
                  aria-label="Text" onChange={(e) => setCaption(e.target.value)} />
        <input className="inp" value={tags.join(' ')} aria-label="Tags" placeholder="#tags for this photo"
               onChange={(e) => setTags(splitsTags(e.target.value))} />
        <div style={{ margin: '4px 0', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {data.thema_tags.map((t) => {
            const aan = tags.some((x) => x.toLowerCase() === t.toLowerCase())
            return <button key={t} type="button" className={aan ? 'btn primary sm' : 'btn ghost sm'}
                           style={{ padding: '0 6px' }} onClick={() => wissel(t)}>{t}</button>
          })}
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <input className="inp" style={{ width: 220 }} value={woorden} placeholder="Keywords (optional)"
                 aria-label="Keywords" onChange={(e) => setWoorden(e.target.value)} />
          <KnopMetReden reden={splitsTags(woorden).length ? null : 'Type keywords first'} onClick={vul}>
            Fill from keywords</KnopMetReden>
          <KnopMetReden reden={data.redenen.goedkeuren} onClick={bewaar}>Save text</KnopMetReden>
          <KnopMetReden reden={goedkeurReden} onClick={keurGoed} primair testid={`goedkeuren-${kaart.content_item_id}`}>
            Approve and schedule</KnopMetReden>
        </div>
      </div>
    </div>
  )
}

// ── Instagram: tab "Posted" (2-11-2026) ───────────────────────────────────────
// 2-11-2026 (Jordy, migratie 0061): "Delete" voor een eigen bot-post (owner/admin). Eén poging via
// de API; met Instagram Login weigert Meta dat volgens de documentatie, dus het normale pad is
// "Open in Instagram" (verwijderen in de app) en daarna "Mark as deleted". Een verwijderde foto
// blijft bezet tot een owner "Allow reuse" doet. Elke stap vraagt eerst een bevestiging.

interface GeplaatstItem {
  id: number; content_item_id: number | null; post_id: number | null; soort: string
  status: 'published' | 'uncertain' | 'deleted' | 'reuse_allowed' | string; media_id: string | null
  permalink: string | null; op: Tijd | null; post_status: string | null
  account?: string; caption_begin?: string
  verwijderd?: 'api' | 'by_hand' | 'failed' | null; verwijderd_op?: Tijd | null; verwijderd_door?: string | null
  verwijder_fout?: string | null; kan_verwijderen?: boolean; kan_markeren?: boolean
  open_in_instagram?: string | null
}
interface VerwijderUitkomst {
  uitkomst: 'api' | 'not_supported' | 'failed'; melding: string
  open_in_instagram: string | null; kan_markeren: boolean
}

/** 2-11 (Jordy): eerlijk vooraf, in het bevestigingsvenster en als tooltip op Delete. */
const DELETE_UITLEG = 'Instagram usually does not allow deleting with this login. One attempt goes via the API. '
  + 'If Instagram refuses, nothing changes: delete the post in the Instagram app, then press Mark as deleted.'

const GEBRUIK_SOORT: Record<string, string> = { photo: 'Photo', reel: 'Reel', story: 'Story' }

/** De post in een bevestiging: account, datum, eerste woorden van de caption en de link. */
const postRegels = (it: GeplaatstItem) => [
  `Account: @${it.account ?? '?'}`,
  `Posted: ${tijd(it.op)}`,
  `Caption: ${it.caption_begin || '(no caption)'}`,
  `Link: ${it.permalink ?? it.open_in_instagram ?? 'no link'}`,
].join('\n')

function GeplaatstStatus({ it }: { it: GeplaatstItem }) {
  if (it.verwijderd === 'api') return <Tag kind="mut">Deleted via API</Tag>
  if (it.verwijderd === 'by_hand') return <Tag kind="mut">Deleted (marked by hand)</Tag>
  const basis = it.status === 'published' ? <Tag kind="ok">Published</Tag>
    : it.status === 'reuse_allowed' ? <Tag kind="mut">Deleted, reuse allowed</Tag>
      : it.status === 'deleted' ? <Tag kind="mut">Deleted</Tag>
        : <PostStatusTag status="onzeker" />
  return (
    <>
      {basis}
      {it.verwijderd === 'failed' ? (
        <div className="mut2" style={{ fontSize: 12, color: 'var(--bad)' }} data-testid={`delete-failed-${it.post_id}`}>
          Delete failed: {it.verwijder_fout ?? 'unknown error'}</div>
      ) : it.verwijder_fout ? (
        <div className="mut2" style={{ fontSize: 12 }} data-testid={`delete-refused-${it.post_id}`}>
          Instagram did not delete it via the API: {it.verwijder_fout}</div>
      ) : null}
    </>
  )
}

function Geplaatst({ account, onGewijzigd }: { account: Account; onGewijzigd?: () => void }) {
  const q = useApi<{ items: GeplaatstItem[]; ik: { nakijken: boolean; verwijderen?: boolean; hergebruik?: boolean } }>(
    `/api/admin/social/accounts/${account.id}/posted`, [account.id])
  const { run, busy, notify } = useActions()
  const [bezig, setBezig] = useState<number | null>(null)
  const items = q.data?.items ?? []
  const ik = q.data?.ik
  /** Ook de accounts verversen: de knop "Posted (n)" telt mee. */
  const herlaad = () => { q.refetch(); onGewijzigd?.() }

  const verwijder = async (it: GeplaatstItem) => {
    if (!it.post_id) return
    const vraag = `Delete this post from Instagram?\n\n${postRegels(it)}\n\n${DELETE_UITLEG}`
    if (!window.confirm(vraag)) return
    setBezig(it.post_id)
    try {
      const r = await api.post<VerwijderUitkomst>(`/api/admin/social/posts/${it.post_id}/delete`)
      notify(r.melding, r.uitkomst === 'api', { duur: r.uitkomst === 'api' ? 5000 : 12000 })
    } catch (e) {
      notify(`Delete: ${e instanceof Error ? e.message : String(e)}`, false)
    } finally {
      setBezig(null)
      herlaad()
    }
  }

  return (
    <div data-testid="geplaatst">
      <div className="mut2" style={{ marginBottom: 6 }}>
        Every photo that Instagram confirmed as posted on this account, and every post with an uncertain result.
        <b> Check on Instagram</b> only reads the status there; it never posts again.
        {ik?.verwijderen ? <> <b>Delete</b> tries once via the Instagram API; Instagram usually does not allow that
          for this login. Then use <b>Open in Instagram</b>, delete it in the app, and press <b>Mark as deleted</b>.
          A deleted photo stays blocked for this account until an owner presses <b>Allow reuse</b>.</> : null}
      </div>
      {q.error ? <ErrorNote error={q.error} /> : null}
      {q.loading && !q.data ? <Loading /> : null}
      <Table cols={[{ key: 'f', label: '' }, { key: 'd', label: 'Date' }, { key: 't', label: 'Type' },
                    { key: 's', label: 'Status' }, { key: 'l', label: '' }]} emptyText="Nothing posted yet.">
        {items.map((it) => (
          <tr key={it.id} data-testid={`posted-row-${it.id}`}>
            <td>{it.content_item_id ? <img src={thumbUrl(it.content_item_id)} alt="" width={40} height={40}
                                           style={{ objectFit: 'cover', borderRadius: 4 }} /> : null}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{tijd(it.op)}</td>
            <td>{GEBRUIK_SOORT[it.soort] ?? it.soort}</td>
            <td><GeplaatstStatus it={it} /></td>
            <td>
              <span className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {it.permalink ? <a href={it.permalink} target="_blank" rel="noreferrer noopener">Open on Instagram</a> : null}
                {it.status === 'published' && !it.permalink ? <span className="mut2">no link</span> : null}
                {it.status === 'uncertain' && it.post_id && ik?.nakijken ? (
                  <button className="btn sm" type="button" disabled={!!busy} data-testid={`nakijken-${it.post_id}`}
                          onClick={() => run({
                            label: 'Check on Instagram',
                            request: mut.post(`/api/admin/social/posts/${it.post_id}/check`),
                            onDone: herlaad, successText: 'Checked',
                          })}>Check on Instagram</button>
                ) : null}
                {it.kan_verwijderen && it.post_id && ik?.verwijderen ? (
                  <button className="btn ghost sm" type="button" disabled={!!busy || bezig !== null}
                          data-testid={`delete-${it.post_id}`} title={DELETE_UITLEG} onClick={() => verwijder(it)}>
                    {bezig === it.post_id ? 'Deleting…' : 'Delete'}</button>
                ) : null}
                {it.kan_markeren && it.post_id && ik?.verwijderen && it.open_in_instagram ? (
                  <a className="btn sm" href={it.open_in_instagram} target="_blank" rel="noreferrer noopener"
                     data-testid={`open-instagram-${it.post_id}`}>Open in Instagram</a>
                ) : null}
                {it.kan_markeren && it.post_id && ik?.verwijderen ? (
                  <button className="btn sm" type="button" disabled={!!busy || bezig !== null}
                          data-testid={`mark-deleted-${it.post_id}`}
                          onClick={() => run({
                            label: 'Mark as deleted',
                            confirm: `Did you delete this post in the Instagram app?\n\n${postRegels(it)}\n\n`
                              + 'Mark it as deleted? The photo stays blocked for this account until an owner '
                              + 'allows reuse.',
                            request: mut.post(`/api/admin/social/posts/${it.post_id}/mark-deleted`),
                            onDone: herlaad, successText: 'Marked as deleted',
                          })}>Mark as deleted</button>
                ) : null}
                {it.status === 'deleted' && ik?.hergebruik ? (
                  <button className="btn ghost sm" type="button" disabled={!!busy || bezig !== null}
                          data-testid={`allow-reuse-${it.id}`}
                          onClick={() => run({
                            label: 'Allow reuse',
                            confirm: `Allow this photo again for @${it.account ?? '?'}?\n\n${postRegels(it)}\n\n`
                              + 'The post was deleted. After this the same photo (also a copy) can be chosen and '
                              + 'posted again on this account.',
                            request: mut.post(`/api/admin/social/usage/${it.id}/allow-reuse`),
                            onDone: herlaad, successText: 'Reuse allowed',
                          })}>Allow reuse</button>
                ) : null}
              </span>
              {it.verwijderd && it.verwijderd !== 'failed' && it.verwijderd_door ? (
                <div className="mut2" style={{ fontSize: 12 }}>by {it.verwijderd_door}, {tijd(it.verwijderd_op ?? null)}</div>
              ) : null}
            </td>
          </tr>
        ))}
      </Table>
    </div>
  )
}

const regels = (t: string) => t.split('\n').map((x) => x.trim()).filter(Boolean)
const procent = (x: number) => `${Math.round(x * 100)}%`

function BijschriftInstellingen({ beheer }: { beheer: boolean }) {
  const q = useApi<BijschriftCfg>('/api/admin/social/captions/config')
  const { run, busy } = useActions()
  const [bewerk, setBewerk] = useState(false)
  const [linkZinnen, setLinkZinnen] = useState('')
  const [meerZinnen, setMeerZinnen] = useState('')
  const [linkAandeel, setLinkAandeel] = useState(0.25)
  const [meerAandeel, setMeerAandeel] = useState(0.33)
  const [verboden, setVerboden] = useState('')
  const c = q.data
  const begin = () => {
    if (!c) return
    setLinkZinnen(c.link_zinnen.join('\n'))
    setMeerZinnen(c.meer_zinnen.join('\n'))
    setLinkAandeel(c.link_aandeel)
    setMeerAandeel(c.meer_aandeel)
    setVerboden(c.verboden.join('\n'))
    setBewerk(true)
  }
  const opslaan = () => run({
    label: 'Caption settings',
    request: mut.post('/api/admin/social/captions/config', {
      link_zinnen: regels(linkZinnen), meer_zinnen: regels(meerZinnen), link_aandeel: linkAandeel,
      meer_aandeel: meerAandeel, verboden: regels(verboden),
    }),
    onDone: () => { setBewerk(false); q.refetch() }, successText: 'Saved',
  })
  const bron = (k: string) => (c?.bron[k] === 'standaard' ? 'default' : 'saved')
  return (
    <details style={{ marginTop: 12 }} data-testid="bijschrift-instellingen">
      <summary>Caption endings, link share and banned list</summary>
      {q.error ? <ErrorNote error={q.error} /> : null}
      {c && !bewerk ? (
        <div style={{ marginTop: 6 }}>
          <div className="mut2">Link in bio on {procent(c.link_aandeel)} of the posts per account ({bron('link_aandeel')}),
            spread evenly. Of the rest, {procent(c.meer_aandeel)} end with a neutral line that there is more to see
            ({bron('meer_aandeel')}). No AI, no offers.</div>
          <div className="mut2" style={{ marginTop: 4 }}>Link sentences ({bron('link_zinnen')}):</div>
          <ul style={{ margin: '4px 0 8px 18px' }}>{c.link_zinnen.map((s) => <li key={s}>{s}</li>)}</ul>
          <div className="mut2">More-to-see sentences ({bron('meer_zinnen')}):</div>
          <ul style={{ margin: '4px 0 8px 18px' }}>{c.meer_zinnen.map((s) => <li key={s}>{s}</li>)}</ul>
          <div className="mut2">Banned words and tags ({c.bron.verboden === 'standaard' ? 'default starter list' : 'saved'}).
            Instagram does not publish an official list; this is our own cautious list. Words that read as an offer
            (set, exclusive, dropped, prices, …) are always refused.</div>
          <div style={{ margin: '4px 0 8px' }}>{c.verboden.join(', ') || '—'}</div>
          {beheer ? <button className="btn sm" type="button" onClick={begin}>Edit</button> : null}
        </div>
      ) : null}
      {bewerk ? (
        <div style={{ marginTop: 6 }}>
          <Field label="Link sentences (one per line, must mention the link in bio)">
            <textarea className="inp" rows={4} value={linkZinnen} onChange={(e) => setLinkZinnen(e.target.value)} /></Field>
          <Field label="More-to-see sentences (one per line, no link, no bio)">
            <textarea className="inp" rows={4} value={meerZinnen} onChange={(e) => setMeerZinnen(e.target.value)} /></Field>
          <Field label="Share of posts with a link in bio (0–1)">
            <input className="inp" type="number" min={0} max={1} step={0.05} value={linkAandeel}
                   onChange={(e) => setLinkAandeel(Number(e.target.value))} /></Field>
          <Field label="Share of the other posts with a more-to-see line (0–1)">
            <input className="inp" type="number" min={0} max={1} step={0.05} value={meerAandeel}
                   onChange={(e) => setMeerAandeel(Number(e.target.value))} /></Field>
          <Field label="Banned words and tags (one per line)">
            <textarea className="inp" rows={6} value={verboden} onChange={(e) => setVerboden(e.target.value)} /></Field>
          <div className="row">
            <button className="btn primary sm" type="button" disabled={!!busy} onClick={opslaan}>Save</button>
            <button className="btn ghost sm" type="button" onClick={() => setBewerk(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
    </details>
  )
}
